import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronRight, Compass, Package, Sparkles, Swords } from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { haptic } from '@/lib/telegram'
import type { EvolutionData } from '@/components/ScentEvolutionPlayer'

interface SimilarProduct {
  id: number
  slug: string | null
  name_uk: string
  name_en: string | null
  price_usd: number
  price_uah: number
  photo_url: string | null
  match_pct: number
  distance: number
  shared_accords: string[]
  shared_notes?: string[]
  match_reason?: string
  vector: Record<string, number>
}

interface Props {
  productId: number | string
  isMayParfum?: boolean
}

export function SimilarFragrances({ productId, isMayParfum = false }: Props) {
  const lang = useAppStore((s) => s.lang)
  const [expandedRadarId, setExpandedRadarId] = useState<number | null>(null)

  // Fetch similar products
  const { data: similar } = useQuery<SimilarProduct[]>({
    queryKey: ['similar-fragrances', productId],
    queryFn: () => api.get<SimilarProduct[]>(`/products/${productId}/similar`),
    staleTime: 5 * 60_000,
  })

  // Fetch current product base vector for dual radar overlay
  const { data: evoData } = useQuery<EvolutionData>({
    queryKey: ['fragrance-evolution', productId],
    queryFn: () => api.get<EvolutionData>(`/products/${productId}/evolution`),
    staleTime: 5 * 60_000,
  })

  // Only show for perfume shops
  if (!isMayParfum) return null
  if (!similar || similar.length === 0) return null

  const targetVector = evoData?.base_vector

  return (
    <section className="mt-8 border-t border-[#444e5d]/40 pt-6">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#d7b171]">
            <Sparkles className="h-4 w-4 text-[#d7b171]" />
            <span>
              {lang === 'en'
                ? 'Similar Olfactory Profiles'
                : lang === 'ru'
                ? 'Похожие по ольфакторному профилю'
                : 'Схожі за ольфакторним профілем'}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-400">
            {lang === 'en'
              ? 'Deep analysis: matched by shared notes, drydown chemistry, and 5D olfactory radar'
              : lang === 'ru'
              ? 'Глубокий анализ: подобрано по общим нотам, аккордам и 5D ольфакторному радару'
              : 'Глибокий аналіз: підібрано за спільними нотами, акордами та 5D ольфакторним радаром'}
          </p>
        </div>

        <Link
          to="/scent-finder"
          onClick={() => haptic('light')}
          className="flex items-center gap-1.5 rounded-full border border-[#bc9050]/40 bg-[#bc9050]/10 px-3 py-1.5 text-xs font-semibold text-[#d7b171] transition hover:bg-[#bc9050]/20 self-start sm:self-auto"
        >
          <Compass className="h-3.5 w-3.5" />
          <span>{lang === 'en' ? 'Open Scent Compass' : 'Відкрити Компас'}</span>
        </Link>
      </div>

      {/* Spacious Horizontal Card Scroll with Full Breakdown */}
      <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 scrollbar-none">
        {similar.map((item) => {
          const name = localizedName(lang, item.name_uk, item.name_en)
          const isRadarOpen = expandedRadarId === item.id
          const pct = item.match_pct
          const pctColor =
            pct >= 88 ? 'text-emerald-400' : pct >= 75 ? 'text-[#d7b171]' : 'text-pink-400'
          const pctBg =
            pct >= 88
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : pct >= 75
              ? 'bg-[#c8a96e]/10 border-[#c8a96e]/30'
              : 'bg-pink-500/10 border-pink-500/30'

          return (
            <div
              key={item.id}
              className="flex w-72 sm:w-80 shrink-0 snap-start flex-col justify-between overflow-hidden rounded-2xl border border-[#444e5d]/60 bg-[#1f242d] p-3.5 text-[#f8f3ea] shadow-xl transition-all hover:border-[#c9a24b]/80"
            >
              <div>
                {/* Header: Photo + Title + Match Badge */}
                <div className="flex items-start gap-3">
                  <Link
                    to="/catalog/$productSlug"
                    params={{ productSlug: item.slug || String(item.id) }}
                    className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[#161922] flex items-center justify-center p-1 group"
                  >
                    {item.photo_url ? (
                      <img
                        src={item.photo_url}
                        alt={name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <Package className="h-7 w-7 text-slate-500" />
                    )}
                  </Link>

                  <div className="flex-1 min-w-0 pr-1">
                    <Link
                      to="/catalog/$productSlug"
                      params={{ productSlug: item.slug || String(item.id) }}
                      className="line-clamp-2 text-xs font-bold leading-snug text-slate-100 hover:text-[#d7b171] transition"
                    >
                      {name}
                    </Link>

                    <p className="mt-1 text-xs font-bold text-[#c9a24b]">
                      {formatPrice(item.price_uah)}
                    </p>

                    <div className="mt-1.5 flex items-center gap-1.5">
                      <div className={`px-2 py-0.5 rounded-lg border text-center ${pctBg}`}>
                        <span className={`text-xs font-bold font-mono leading-none ${pctColor}`}>
                          {pct}%
                        </span>
                        <span className="text-[8px] uppercase tracking-wider text-neutral-400 ml-1">
                          {lang === 'en' ? 'Match' : 'Збіг'}
                        </span>
                      </div>

                      {/* Radar Toggle Button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          haptic('light')
                          setExpandedRadarId((prev) => (prev === item.id ? null : item.id))
                        }}
                        className={`px-2 py-0.5 rounded-lg border text-[10px] font-semibold transition ${
                          isRadarOpen
                            ? 'bg-[#06b6d4]/20 border-[#06b6d4] text-[#06b6d4]'
                            : 'bg-white/5 border-white/10 text-neutral-400 hover:text-white'
                        }`}
                      >
                        {isRadarOpen ? '✕ Радар' : '📊 Радар'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Interactive Dual Radar Overlay (Shown when toggled or default) */}
                {isRadarOpen && (
                  <div className="mt-3 pt-3 border-t border-white/10 animate-in fade-in duration-200">
                    <MiniOlfactoryRadar
                      targetVec={targetVector}
                      similarVec={item.vector}
                      targetName={lang === 'en' ? 'Current' : 'Цей парфум'}
                      similarName={lang === 'en' ? 'Similar' : 'Схожий'}
                    />
                  </div>
                )}

                {/* Similarity Breakdown ("Чим саме схожі") */}
                <div className="mt-3 pt-2.5 border-t border-white/10 space-y-2 text-[11px]">
                  {/* Shared Notes */}
                  {item.shared_notes && item.shared_notes.length > 0 && (
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 block mb-1">
                        🌿 {lang === 'en' ? 'Shared Notes:' : 'Спільні ноти:'}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {item.shared_notes.map((note, idx) => (
                          <span
                            key={idx}
                            className="rounded-md bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300"
                          >
                            {note}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shared Accords */}
                  {item.shared_accords && item.shared_accords.length > 0 && (
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#d7b171] block mb-1">
                        ✨ {lang === 'en' ? 'Shared Accords:' : 'Спільні акорди:'}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {item.shared_accords.map((accord, idx) => (
                          <span
                            key={idx}
                            className="rounded-md bg-[#bc9050]/15 border border-[#bc9050]/30 px-1.5 py-0.5 text-[10px] font-medium text-[#ebd5ab]"
                          >
                            #{accord}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Match Rationale Callout */}
                  {item.match_reason && (
                    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2 text-[10px] text-neutral-300 leading-relaxed">
                      <span className="font-semibold text-[#c9a24b]">Ольфакторна спорідненість: </span>
                      <span>{item.match_reason}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Actions */}
              <div className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-between gap-2">
                <Link
                  to="/scent-finder"
                  onClick={() => haptic('medium')}
                  className="flex items-center gap-1 text-[11px] font-semibold text-neutral-400 hover:text-[#06b6d4] transition"
                  title="Порівняти в ольфакторному батлі"
                >
                  <Swords className="h-3.5 w-3.5 text-[#06b6d4]" />
                  <span>{lang === 'en' ? 'Versus Battle' : 'Батл'}</span>
                </Link>

                <Link
                  to="/catalog/$productSlug"
                  params={{ productSlug: item.slug || String(item.id) }}
                  onClick={() => haptic('light')}
                  className="flex items-center gap-1 text-[11px] font-semibold text-[#c9a24b] hover:text-[#ebd5ab] transition"
                >
                  <span>{lang === 'en' ? 'Details' : 'Детальніше'}</span>
                  <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function MiniOlfactoryRadar({
  targetVec,
  similarVec,
  targetName = 'Цей аромат',
  similarName = 'Схожий',
}: {
  targetVec?: Record<string, number>
  similarVec: Record<string, number>
  targetName?: string
  similarName?: string
}) {
  const axes: (keyof typeof AXIS_LABELS)[] = ['freshness', 'sharpness', 'sillage', 'sweetness', 'formality']
  const AXIS_LABELS: Record<string, string> = {
    freshness: 'Свіж',
    sharpness: 'Різк',
    sillage: 'Шлейф',
    sweetness: 'Солод',
    formality: 'Форм',
  }

  const cx = 65
  const cy = 60
  const r = 42

  const defaultTarget = { freshness: 0.5, sharpness: 0.5, sillage: 0.5, sweetness: 0.5, formality: 0.5 }
  const tVec = targetVec || defaultTarget

  const getPoints = (vec: Record<string, number>) => {
    return axes
      .map((key, i) => {
        const val = Math.max(0.12, Math.min(1.0, vec[key] ?? 0.5))
        const angle = -Math.PI / 2 + (i * 2 * Math.PI) / 5
        const x = cx + r * val * Math.cos(angle)
        const y = cy + r * val * Math.sin(angle)
        return `${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
  }

  const targetPoints = getPoints(tVec)
  const similarPoints = getPoints(similarVec)

  return (
    <div className="flex flex-col items-center p-2 rounded-xl bg-black/40 border border-white/10">
      <svg width={130} height={125} className="overflow-visible">
        {/* Background Web Rings */}
        {[0.33, 0.66, 1.0].map((step, idx) => (
          <circle
            key={idx}
            cx={cx}
            cy={cy}
            r={r * step}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeDasharray={step < 1 ? '2,2' : undefined}
          />
        ))}

        {/* Axis Spokes and Labels */}
        {axes.map((key, i) => {
          const angle = -Math.PI / 2 + (i * 2 * Math.PI) / 5
          const x = cx + r * Math.cos(angle)
          const y = cy + r * Math.sin(angle)
          const lx = cx + (r + 10) * Math.cos(angle)
          const ly = cy + (r + 10) * Math.sin(angle)

          return (
            <g key={key}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(255,255,255,0.15)" strokeWidth={1} />
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={8}
                fill="#94a3b8"
                fontWeight="bold"
              >
                {AXIS_LABELS[key]}
              </text>
            </g>
          )
        })}

        {/* Target Fragrance Polygon (Gold) */}
        {targetVec && (
          <polygon
            points={targetPoints}
            fill="rgba(201, 162, 75, 0.25)"
            stroke="#c9a24b"
            strokeWidth={1.8}
          />
        )}

        {/* Similar Fragrance Polygon (Cyan) */}
        <polygon
          points={similarPoints}
          fill="rgba(6, 182, 212, 0.25)"
          stroke="#06b6d4"
          strokeWidth={1.8}
        />
      </svg>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-1 text-[9px] font-semibold">
        <span className="flex items-center gap-1 text-[#c9a24b]">
          <span className="inline-block w-2 h-2 rounded-full bg-[#c9a24b]" />
          <span>{targetName}</span>
        </span>
        <span className="flex items-center gap-1 text-[#06b6d4]">
          <span className="inline-block w-2 h-2 rounded-full bg-[#06b6d4]" />
          <span>{similarName}</span>
        </span>
      </div>
    </div>
  )
}
